import React, { useMemo } from 'react';
import { T, panelStyle, GradientTitle, SparkLine, MiniBar, StatusDot } from './NOCShared';
import { CircularGauge } from './NOCGauges';
import { formatTime } from '../../utils/formatTime';

export default function NOCLeftPanel({
  dashboardData, alerts, securityEvents, workers, learningStats,
  networkIO, diskIO, systemMetrics, services, serverList,
}) {
  const system = dashboardData?.system || {};

  const cpuHistory = useMemo(() => services.map(s => s.cpu || 0).slice(-30), [services]);
  const memHistory = useMemo(() => services.map(s => s.memory || 0).slice(-30), [services]);

  const activeAlerts = alerts.filter(a => a.severity === 'critical' || a.severity === 'high');
  const normalAlerts = alerts.filter(a => a.severity !== 'critical' && a.severity !== 'high');

  const remediationTasks = useMemo(() => {
    const tasks = [];
    if (workers) {
      workers.filter(w => w.current_task).slice(0, 5).forEach(w => {
        tasks.push({ text: `${w.name}: ${w.current_task}`, status: w.status === 'busy' ? 'running' : 'done' });
      });
    }
    if (learningStats) {
      if (learningStats.total_resolved) tasks.push({ text: `AI resolved ${learningStats.total_resolved} incidents`, status: 'done' });
      if (learningStats.autonomous_fixes) tasks.push({ text: `Auto-fix: ${learningStats.autonomous_fixes} actions`, status: 'done' });
    }
    if (tasks.length === 0) tasks.push({ text: 'Monitoring idle — no active jobs', status: 'idle' });
    return tasks;
  }, [workers, learningStats]);

  return (
    <div className="flex flex-col gap-1.5 overflow-y-auto h-full" style={{ scrollbarWidth: 'thin', paddingRight: 2 }}>
      {/* Panel 1: AI-Powered System Health */}
      <div style={{ ...panelStyle, padding: 12 }}>
        <GradientTitle text="System Health" />
        <div className="flex justify-center gap-2" style={{ marginBottom: 8 }}>
          <CircularGauge value={system.cpu_usage || 0} label="CPU" size={72} strokeWidth={5} />
          <CircularGauge value={system.memory_usage || 0} label="Memory" size={72} strokeWidth={5} />
          <CircularGauge value={system.disk_usage || 0} label="Disk" size={72} strokeWidth={5} />
        </div>
        <div className="grid grid-cols-3 gap-1 text-center">
          {[
            { label: 'CPU Trend', data: cpuHistory },
            { label: 'Mem Trend', data: memHistory },
            { label: 'Disk Usage', stat: `${system.disk_usage || 0}%`, statColor: (system.disk_usage || 0) > 70 ? T.warning : T.success },
          ].map((item, i) => (
            <div key={i} style={{ background: 'rgba(255,255,255,0.02)', padding: 4, borderRadius: 6 }}>
              <div className="text-[8px] uppercase tracking-wider" style={{ color: T.textDim, marginBottom: 2 }}>{item.label}</div>
              {item.data ? <SparkLine data={item.data} height={22} /> : <div className="text-[10px] font-mono" style={{ color: item.statColor || T.textBright }}>{item.stat}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Panel 2: Anomaly Detection */}
      <div style={{ ...panelStyle, padding: 12 }} className="flex-1 min-h-0 flex flex-col">
        <GradientTitle text="Anomaly Detection" />
        <div className="flex items-center gap-2" style={{ marginBottom: 8 }}>
          <div className="flex items-center gap-1">
            <StatusDot status="running" />
            <span className="text-[9px]" style={{ color: activeAlerts.length > 0 ? T.danger : T.success }}>
              {activeAlerts.length > 0 ? `${activeAlerts.length} anomalies` : 'All systems normal'}
            </span>
          </div>
          <div className="flex-1 text-right text-[8px]" style={{ color: T.textDim }}>
            {alerts.length} alerts | {securityEvents.length} sec events
          </div>
        </div>
        <div className="flex-1 overflow-y-auto font-mono text-[8px]" style={{ scrollbarWidth: 'thin', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[...activeAlerts, ...normalAlerts, ...securityEvents].slice(0, 20).map((item, idx) => {
            const isCritical = item.severity === 'critical' || item.severity === 'high';
            const ts = item.fired_at || item.timestamp || item.created_at || '';
            return (
              <div key={idx} className="flex gap-1.5" style={{ background: isCritical ? 'rgba(239,68,68,0.08)' : 'transparent', padding: '2px 4px', borderRadius: 4 }}>
                <span style={{ color: isCritical ? T.danger : T.textDim }}>{ts ? formatTime(ts, { timeOnly: true }) : '--:--:--'}</span>
                <span className="truncate" style={{ color: isCritical ? T.textBright : T.textDim }}>
                  {item.name || item.message || item.description || item.event_type || 'Event'}
                </span>
              </div>
            );
          })}
          {alerts.length === 0 && securityEvents.length === 0 && (
            <div className="text-center" style={{ color: T.textDim, padding: '16px 0' }}>Log stream idle — no events detected</div>
          )}
        </div>
      </div>

      {/* Panel 3: Auto-Remediation */}
      <div style={{ ...panelStyle, padding: 12 }}>
        <GradientTitle text="Auto-Remediation Jobs" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {remediationTasks.map((task, idx) => {
            const statusColor = task.status === 'running' ? T.warning : task.status === 'done' ? T.success : T.textDim;
            return (
              <div key={idx} className="flex items-center gap-2" style={{ background: 'rgba(255,255,255,0.02)', padding: '4px 6px', borderRadius: 6 }}>
                <div className="flex-shrink-0" style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: statusColor,
                  boxShadow: task.status === 'running' ? `0 0 6px ${T.warning}` : `0 0 4px ${statusColor}`,
                  animation: task.status === 'running' ? 'pulse 1.5s infinite' : 'none',
                }} />
                <span className="text-[9px] flex-1 truncate" style={{ color: T.text }}>{task.text}</span>
                <span className="text-[8px] font-semibold uppercase tracking-wider" style={{ color: statusColor }}>
                  [{task.status === 'running' ? 'RUNNING' : task.status === 'done' ? 'SUCCESS' : 'IDLE'}]
                </span>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-between text-[8px]" style={{ color: T.textDim, marginTop: 8 }}>
          <span>Workers: {workers.filter(w => w.status === 'online' || w.status === 'running').length}/{workers.length} online</span>
          <span>Learning: {learningStats?.total_resolved || 0} resolved</span>
        </div>
      </div>

      {/* Active Monitoring Agents — only show agents with active > 0 */}
      {(() => {
        const allExp = serverList.flatMap(s => s.exporters || []);
        const activeAgents = [...new Set(allExp.filter(e => e.active).map(e => e.name))].sort();
        if (activeAgents.length === 0) return null;
        return (
          <div style={{ ...panelStyle, padding: 8 }}>
            <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
              <span className="text-[8px] font-semibold uppercase tracking-wider" style={{ color: T.textDim }}>Active Agents</span>
              <span className="text-[7px] font-mono" style={{ color: T.purple }}>{activeAgents.length} types</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {activeAgents.map((name) => {
                const count = serverList.filter(s => s.exporters?.some(e => e.name === name && e.active)).length;
                return (
                  <div key={name} className="flex items-center gap-1" style={{ background: 'rgba(162,70,255,0.08)', border: `1px solid ${T.borderGlow}`, padding: '2px 6px', borderRadius: 6 }}>
                    <div style={{ width: 4, height: 4, borderRadius: '50%', background: T.pink, boxShadow: `0 0 4px ${T.pink}` }} />
                    <span className="text-[8px] font-medium" style={{ color: T.text }}>{name}</span>
                    <span className="text-[8px] font-mono" style={{ color: T.purple }}>{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
